import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Joyride, { ACTIONS, CallBackProps, EVENTS, STATUS, Step } from 'react-joyride';
import { EventAddOn, EventNotificationSettings, EventSession, EventStatus, EventTicketType, TicketMode, Venue, clubsBookingApi } from '@/api/clubs-booking';
import AdminShell from '@/components/admin/AdminShell';
import { formatCurrencyFromCents } from '@/utils/currency';
import { useStripeConnectStatus } from '@/contexts/StripeConnectStatusContext';

const emptyVenueForm = {
  name: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  postcode: '',
  country: 'United Kingdom',
};
const CREATE_NEW_VENUE_SELECT_VALUE = '__create_new_venue__';

const steps = ['Information', 'Sessions', 'Tickets', 'Settings'];
const reminderLeadOptions = [
  { value: 30, label: '30 minutes before' },
  { value: 60, label: '1 hour before' },
  { value: 120, label: '2 hours before' },
  { value: 180, label: '3 hours before' },
  { value: 360, label: '6 hours before' },
  { value: 720, label: '12 hours before' },
  { value: 1440, label: '24 hours before' },
  { value: 2880, label: '48 hours before' },
];

const activityCreateTourStepsByTab: Record<number, Step[]> = {
  0: [
    {
      target: '[data-tour-create-tabs]',
      placement: 'bottom',
      disableBeacon: true,
      content: 'Use these tabs to build your activity step by step.',
    },
    {
      target: '[data-tour-create-title]',
      placement: 'bottom',
      disableBeacon: true,
      content: 'Start with a clear title and description so parents understand what they are booking.',
    },
    {
      target: '[data-tour-create-venue]',
      placement: 'top',
      disableBeacon: true,
      content: 'Select a venue or create one here before moving on.',
    },
  ],
  1: [
    {
      target: '[data-tour-create-session-form]',
      placement: 'top',
      disableBeacon: true,
      content: 'Add session dates and times. These are the specific events that people will attend, so start by setting Start, End, and Capacity.',
    },
    {
      target: '[data-tour-create-session-add]',
      placement: 'top',
      disableBeacon: true,
      content: 'Use Add session for a single date/time session once those fields are filled in.',
    },
    {
      target: '[data-tour-create-session-recurring]',
      placement: 'top',
      disableBeacon: true,
      content: 'Use Add recurring sessions to generate weekly or daily sessions in one go instead of creating each one manually.',
    },
  ],
  2: [
    {
      target: '[data-tour-create-ticket-quick]',
      placement: 'top',
      disableBeacon: true,
      content: 'Tickets are what parents see on your booking page, and are what parents pay for. You can have tickets per session, or a ticket for the entire activity, or both.',
    },
  ],
  3: [
    {
      target: '[data-tour-create-settings]',
      placement: 'top',
      disableBeacon: true,
      content: 'Choose payment methods and booking rules before you publish. Remember that you need to set up a Stripe account in settings before online payments will work.',
    },
    {
      target: '[data-tour-create-submit]',
      placement: 'left',
      disableBeacon: true,
      content: 'Save your activity when everything looks right.',
    },
  ],
};

const ActivityCreatePage = () => {
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const isEditing = !!eventId;

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [savingSessionId, setSavingSessionId] = useState('');
  const [showCreateTour, setShowCreateTour] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);
  const [tourSteps, setTourSteps] = useState<Step[]>([]);
  const [hasExistingActivities, setHasExistingActivities] = useState(isEditing);
  const [activityCountLoading, setActivityCountLoading] = useState(!isEditing);
  const [eventLoading, setEventLoading] = useState(false);
  const [venuesLoading, setVenuesLoading] = useState(true);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [error, setError] = useState('');
  const [showVenueModal, setShowVenueModal] = useState(false);
  const [venueModalMode, setVenueModalMode] = useState<'create' | 'edit'>('create');
  const [editingVenueId, setEditingVenueId] = useState('');
  const [venueSaving, setVenueSaving] = useState(false);
  const [venueError, setVenueError] = useState('');
  const [venueForm, setVenueForm] = useState(emptyVenueForm);

  const [newSession, setNewSession] = useState({
    startDateTime: '',
    endDateTime: '',
    capacity: 20,
    unlimited: false,
  });
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [recurringForm, setRecurringForm] = useState({
    startDate: '',
    startTime: '15:30',
    endTime: '16:30',
    frequency: 'weekly' as 'weekly' | 'daily',
    occurrences: 6,
    capacity: 20,
    unlimited: false,
  });
  const [newTicket, setNewTicket] = useState({ name: '', mode: 'all_sessions' as TicketMode, price: '0.00', sessionIds: [] as string[] });
  const [quickTicketSetup, setQuickTicketSetup] = useState({
    includePerSession: true,
    perSessionPrice: '0.00',
    includeAllSessions: true,
    allSessionsPrice: '0.00',
  });
  const [newAddOn, setNewAddOn] = useState({ name: '', price: '0.00' });
  const [sessionFormError, setSessionFormError] = useState('');
  const [sessionFieldErrors, setSessionFieldErrors] = useState({
    startDateTime: false,
    endDateTime: false,
    capacity: false,
  });

  const [activity, setActivity] = useState({
    title: '',
    description: '',
    venueId: '',
    status: 'draft' as EventStatus,
    currency: 'GBP',
    paymentMethods: ['card', 'cash'] as Array<'card' | 'cash'>,
    bookingEnabled: true,
    bookingCutoffHours: '',
    waitlistEnabled: false,
    sessions: [] as EventSession[],
    ticketTypes: [] as EventTicketType[],
    addOns: [] as EventAddOn[],
    notificationSettings: {
      emailEnabled: false,
      smsEnabled: false,
      emailLeadMinutes: 1440,
      smsLeadMinutes: 120,
    } as EventNotificationSettings,
  });

  const selectedVenue = useMemo(() => venues.find((venue) => venue._id === activity.venueId), [venues, activity.venueId]);
  const { loading: stripeStatusLoading, isStripeReady: stripeCardReady } = useStripeConnectStatus();
  const hasNoVenues = !venuesLoading && venues.length === 0;
  const formatPrice = (amountCents: number, currencyCode: string) => {
    return formatCurrencyFromCents(amountCents, currencyCode || 'GBP');
  };
  const isUnlimitedCapacity = (capacity: number) => Number(capacity) < 0;

  const dedupeAndAppendSessions = (incomingSessions: EventSession[]) => {
    setActivity((prev) => {
      const existing = new Set(prev.sessions.map((session) => `${session.startDateTime}|${session.endDateTime}`));
      const uniqueIncoming = incomingSessions.filter((session) => !existing.has(`${session.startDateTime}|${session.endDateTime}`));
      return {
        ...prev,
        sessions: [...prev.sessions, ...uniqueIncoming].sort((a, b) => +new Date(a.startDateTime) - +new Date(b.startDateTime)),
      };
    });
  };

  const combineDateAndTime = (date: string, time: string) => `${date}T${time}`;
  const toDateTimeInput = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };
  const toDateTimeInputValue = (value: string) => {
    if (!value) return '';
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) {
      return value.slice(0, 16);
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return toDateTimeInput(parsed);
  };
  const toUtcIsoString = (value: string) => {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toISOString();
  };
  const formatSessionTicketName = (session: EventSession) => {
    const start = new Date(session.startDateTime);
    const dateLabel = start.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
    const startTime = start.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    return `${dateLabel} ${startTime}`;
  };
  const parseCutoffHours = (value: string): number | undefined => {
    if (!value.trim()) return undefined;
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) return undefined;
    return Math.floor(parsed);
  };

  const loadVenues = async () => {
    setVenuesLoading(true);
    try {
      const data = await clubsBookingApi.listMyVenues();
      setVenues(data.items || []);
    } catch {
      setVenues([]);
    } finally {
      setVenuesLoading(false);
    }
  };

  useEffect(() => {
    void loadVenues();
  }, []);

  useEffect(() => {
    if (isEditing) return;

    let active = true;
    const loadActivityCount = async () => {
      setActivityCountLoading(true);
      try {
        const data = await clubsBookingApi.listMyEvents();
        if (!active) return;
        setHasExistingActivities((data.items || []).length > 0);
      } catch {
        if (!active) return;
        // Fail closed so we don't show onboarding unexpectedly when data is unavailable.
        setHasExistingActivities(true);
      } finally {
        if (active) setActivityCountLoading(false);
      }
    };

    void loadActivityCount();
    return () => {
      active = false;
    };
  }, [isEditing]);

  useEffect(() => {
    if (isEditing || activityCountLoading || hasExistingActivities || eventLoading) {
      setShowCreateTour(false);
      return;
    }

    const stepsForTab = activityCreateTourStepsByTab[step] || [];
    if (!stepsForTab.length) {
      setShowCreateTour(false);
      return;
    }

    setTourSteps(stepsForTab);
    setTourStepIndex(0);
    setShowCreateTour(true);
  }, [isEditing, activityCountLoading, hasExistingActivities, eventLoading, step]);

  useEffect(() => {
    const loadEvent = async () => {
      if (!eventId) return;
      setEventLoading(true);
      setError('');
      try {
        const event = await clubsBookingApi.getMyEvent(eventId);
        const sessionBookingFlags = (event.sessions || []).map((session) => session.bookingEnabled !== false);
        const bookingEnabled = sessionBookingFlags.length ? sessionBookingFlags.every(Boolean) : true;
        const bookingCutoffCandidates = (event.sessions || [])
          .map((session) => (typeof session.bookingCutoffHours === 'number' ? session.bookingCutoffHours : undefined))
          .filter((value): value is number => value !== undefined);
        const bookingCutoffHours = bookingCutoffCandidates.length ? String(bookingCutoffCandidates[0]) : '';
        const sessions = (event.sessions && event.sessions.length > 0)
          ? event.sessions
          : [{
              id: crypto.randomUUID(),
              startDateTime: event.startDate,
              endDateTime: event.endDate,
              capacity: event.capacity,
            }];
        const ticketTypes = (event.ticketTypes && event.ticketTypes.length > 0)
          ? event.ticketTypes
          : [];
        setActivity({
          title: event.title || '',
          description: event.description || '',
          venueId: event.venueId || '',
          status: event.status || 'draft',
          currency: event.currency || 'GBP',
          paymentMethods: Array.from(
            new Set((event.paymentMethods || ['card', 'cash']).filter((method) => method === 'card' || method === 'cash')),
          ) as Array<'card' | 'cash'>,
          bookingEnabled,
          bookingCutoffHours,
          waitlistEnabled: Boolean(event.waitlistEnabled),
          sessions,
          ticketTypes,
          addOns: event.addOns || [],
          notificationSettings: event.notificationSettings || {
            emailEnabled: false,
            smsEnabled: false,
            emailLeadMinutes: 1440,
            smsLeadMinutes: 120,
          },
        });
      } catch (err: any) {
        const messageText = err?.response?.data?.message;
        setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not load activity');
      } finally {
        setEventLoading(false);
      }
    };

    void loadEvent();
  }, [eventId]);

  const closeVenueModal = () => {
    setShowVenueModal(false);
    setVenueModalMode('create');
    setEditingVenueId('');
    setVenueError('');
    setVenueForm(emptyVenueForm);
  };

  const openCreateVenueModal = () => {
    setVenueModalMode('create');
    setEditingVenueId('');
    setVenueError('');
    setVenueForm(emptyVenueForm);
    setShowVenueModal(true);
  };

  const openEditVenueModal = () => {
    if (!selectedVenue) return;
    setVenueModalMode('edit');
    setEditingVenueId(selectedVenue._id);
    setVenueError('');
    setVenueForm({
      name: selectedVenue.name,
      addressLine1: selectedVenue.addressLine1,
      addressLine2: selectedVenue.addressLine2 || '',
      city: selectedVenue.city,
      postcode: selectedVenue.postcode,
      country: selectedVenue.country || 'United Kingdom',
    });
    setShowVenueModal(true);
  };

  const handleVenueSelectChange = (value: string) => {
    if (value === CREATE_NEW_VENUE_SELECT_VALUE) {
      openCreateVenueModal();
      return;
    }
    setActivity((current) => ({ ...current, venueId: value }));
  };

  const submitVenue = async (e: FormEvent) => {
    e.preventDefault();
    setVenueSaving(true);
    setVenueError('');
    try {
      if (venueModalMode === 'edit') {
        const updatedVenue = await clubsBookingApi.updateVenue(editingVenueId, venueForm);
        setVenues((list) => list.map((venue) => (venue._id === updatedVenue._id ? updatedVenue : venue)).sort((a, b) => a.name.localeCompare(b.name)));
      } else {
        const createdVenue = await clubsBookingApi.createVenue(venueForm);
        setVenues((list) => [...list, createdVenue].sort((a, b) => a.name.localeCompare(b.name)));
        setActivity((value) => ({ ...value, venueId: createdVenue._id }));
      }
      closeVenueModal();
    } catch (err: any) {
      const messageText = err?.response?.data?.message;
      setVenueError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not save venue');
    } finally {
      setVenueSaving(false);
    }
  };

  const addRecurringSessions = () => {
    if (!recurringForm.startDate) return;
    const start = combineDateAndTime(recurringForm.startDate, recurringForm.startTime);
    const end = combineDateAndTime(recurringForm.startDate, recurringForm.endTime);
    if (+new Date(end) <= +new Date(start)) {
      setError('Recurring sessions need an end time after start time');
      return;
    }
    const stepDays = recurringForm.frequency === 'daily' ? 1 : 7;
    const sessions: EventSession[] = Array.from({ length: Math.max(1, recurringForm.occurrences) }).map((_, idx) => {
      const startDate = new Date(start);
      startDate.setDate(startDate.getDate() + (idx * stepDays));
      const endDate = new Date(end);
      endDate.setDate(endDate.getDate() + (idx * stepDays));
      return {
        id: crypto.randomUUID(),
        startDateTime: toDateTimeInput(startDate),
        endDateTime: toDateTimeInput(endDate),
        capacity: recurringForm.unlimited ? -1 : Number(recurringForm.capacity),
      };
    });
    dedupeAndAppendSessions(sessions);
    setShowRecurringModal(false);
  };

  const addSession = () => {
    const nextFieldErrors = {
      startDateTime: !newSession.startDateTime,
      endDateTime: !newSession.endDateTime,
      capacity: false,
    };

    if (nextFieldErrors.startDateTime || nextFieldErrors.endDateTime) {
      setSessionFieldErrors(nextFieldErrors);
      setSessionFormError('Add a start date and end date before adding a session.');
      return;
    }

    const startMs = +new Date(newSession.startDateTime);
    const endMs = +new Date(newSession.endDateTime);
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
      setSessionFieldErrors({
        startDateTime: true,
        endDateTime: true,
        capacity: false,
      });
      setSessionFormError('Session end must be after start.');
      return;
    }

    if (!newSession.unlimited && (!Number.isFinite(Number(newSession.capacity)) || Number(newSession.capacity) < 1)) {
      setSessionFieldErrors({
        startDateTime: false,
        endDateTime: false,
        capacity: true,
      });
      setSessionFormError('Session capacity must be at least 1, or use unlimited.');
      return;
    }

    setSessionFormError('');
    setSessionFieldErrors({
      startDateTime: false,
      endDateTime: false,
      capacity: false,
    });
    setError('');
    setActivity((prev) => ({
      ...prev,
      sessions: [
        ...prev.sessions,
        {
          id: crypto.randomUUID(),
          startDateTime: newSession.startDateTime,
          endDateTime: newSession.endDateTime,
          capacity: newSession.unlimited ? -1 : Number(newSession.capacity),
        },
      ],
    }));
    setNewSession({
      startDateTime: '',
      endDateTime: '',
      capacity: 20,
      unlimited: false,
    });
  };

  const updateSession = (sessionId: string, updates: Partial<EventSession>) => {
    setActivity((prev) => ({
      ...prev,
      sessions: prev.sessions.map((session) => (
        session.id === sessionId
          ? { ...session, ...updates }
          : session
      )),
    }));
    if (sessionFormError) {
      setSessionFormError('');
    }
  };

  const removeSession = (sessionId: string) => {
    setActivity((v) => ({
      ...v,
      sessions: v.sessions.filter((item) => item.id !== sessionId),
      ticketTypes: v.ticketTypes.map((ticket) => ({
        ...ticket,
        sessionIds: (ticket.sessionIds || []).filter((id) => id !== sessionId),
      })),
    }));
  };

  const requestRemoveSession = (session: EventSession) => {
    if (Number(session.bookedSpots || 0) > 0) {
      setSessionFormError('Sessions with existing bookings cannot be removed.');
      return;
    }
    const startLabel = session.startDateTime
      ? new Date(session.startDateTime).toLocaleString('en-GB')
      : 'this session';
    const confirmed = window.confirm(
      `Remove ${startLabel}?\n\nThis will remove the session from this activity and any ticket rules that reference it. This cannot be undone after you save the activity.`,
    );
    if (!confirmed) return;
    removeSession(session.id);
  };

  const validateSessionRows = (sessions: EventSession[]) => {
    const invalidSession = sessions.find((session) => {
      const startMs = +new Date(session.startDateTime);
      const endMs = +new Date(session.endDateTime);
      return (
        !session.startDateTime ||
        !session.endDateTime ||
        !Number.isFinite(startMs) ||
        !Number.isFinite(endMs) ||
        endMs <= startMs ||
        (!isUnlimitedCapacity(session.capacity) && (!Number.isFinite(Number(session.capacity)) || Number(session.capacity) < 1))
      );
    });
    if (!invalidSession) return '';
    return 'Check each session has a valid start, end, and capacity before saving.';
  };

  const addTicket = () => {
    if (!newTicket.name.trim()) return;
    const priceCents = Math.round(Number(newTicket.price) * 100);
    if (!Number.isFinite(priceCents) || priceCents < 0) return;
    setActivity((prev) => ({
      ...prev,
      ticketTypes: [
        ...prev.ticketTypes,
        {
          id: crypto.randomUUID(),
          name: newTicket.name.trim(),
          mode: newTicket.mode,
          priceCents,
          sessionIds: newTicket.mode === 'all_sessions' ? prev.sessions.map((session) => session.id) : newTicket.sessionIds,
        },
      ],
    }));
    setNewTicket({ name: '', mode: 'all_sessions', price: '0.00', sessionIds: [] });
  };

  const addAddOn = () => {
    if (!newAddOn.name.trim()) return;
    const priceCents = Math.round(Number(newAddOn.price) * 100);
    if (!Number.isFinite(priceCents) || priceCents < 0) return;
    setActivity((prev) => ({
      ...prev,
      addOns: [
        ...prev.addOns,
        {
          id: crypto.randomUUID(),
          name: newAddOn.name.trim(),
          priceCents,
        },
      ],
    }));
    setNewAddOn({ name: '', price: '0.00' });
  };

  const createQuickDefaultTickets = () => {
    if (!activity.sessions.length) {
      setError('Add at least one session before creating tickets');
      return;
    }
    if (!quickTicketSetup.includePerSession && !quickTicketSetup.includeAllSessions) {
      setError('Select at least one ticket type to create');
      return;
    }
    const perSessionCents = Math.round(Number(quickTicketSetup.perSessionPrice) * 100);
    if (quickTicketSetup.includePerSession && (!Number.isFinite(perSessionCents) || perSessionCents < 0)) {
      setError('Enter a valid per-session ticket price');
      return;
    }
    const allSessionsCents = Math.round(Number(quickTicketSetup.allSessionsPrice) * 100);
    if (quickTicketSetup.includeAllSessions && (!Number.isFinite(allSessionsCents) || allSessionsCents < 0)) {
      setError('Enter a valid all-sessions ticket price');
      return;
    }

    const generatedTickets: EventTicketType[] = [];
    if (quickTicketSetup.includePerSession) {
      generatedTickets.push(...activity.sessions.map((session) => ({
        id: crypto.randomUUID(),
        name: `One session - ${formatSessionTicketName(session)}`,
        mode: 'specific_sessions' as TicketMode,
        priceCents: perSessionCents,
        sessionIds: [session.id],
      })));
    }

    if (quickTicketSetup.includeAllSessions) {
      generatedTickets.unshift({
        id: crypto.randomUUID(),
        name: 'All sessions',
        mode: 'all_sessions',
        priceCents: allSessionsCents,
        sessionIds: activity.sessions.map((session) => session.id),
      });
    }

    setActivity((prev) => ({
      ...prev,
      ticketTypes: generatedTickets,
    }));
    setError('');
  };

  const handleCreateTourCallback = (data: CallBackProps) => {
    const { action, index, status, type } = data;
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setShowCreateTour(false);
      return;
    }

    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      const nextIndex = action === ACTIONS.PREV ? index - 1 : index + 1;
      if (nextIndex < 0 || nextIndex >= tourSteps.length) {
        setShowCreateTour(false);
        return;
      }
      setTourStepIndex(nextIndex);
    }
  };

  const saveActivity = async (options: { navigateAfterSave?: boolean } = {}) => {
    setSaving(true);
    setError('');
    try {
      if (!activity.sessions.length) throw new Error('Add at least one session');
      if (!activity.ticketTypes.length) throw new Error('Add at least one ticket type');
      if (!activity.paymentMethods.length) throw new Error('Select at least one payment method');
      const sessionValidationError = validateSessionRows(activity.sessions);
      if (sessionValidationError) throw new Error(sessionValidationError);
      const sessionsSorted = [...activity.sessions].sort((a, b) => +new Date(a.startDateTime) - +new Date(b.startDateTime));
      const first = sessionsSorted[0];
      const last = sessionsSorted[sessionsSorted.length - 1];
      const hasUnlimitedSessions = activity.sessions.some((session) => isUnlimitedCapacity(session.capacity));
      const capacity = hasUnlimitedSessions ? -1 : activity.sessions.reduce((total, session) => total + Number(session.capacity || 0), 0);
      const priceCents = activity.ticketTypes[0].priceCents;
      const bookingCutoffHours = parseCutoffHours(activity.bookingCutoffHours);
      if (activity.bookingCutoffHours.trim() && bookingCutoffHours === undefined) {
        throw new Error('Booking cutoff must be 0 or greater');
      }
      const normalizedSessions = activity.sessions.map((session) => ({
        id: session.id,
        startDateTime: toUtcIsoString(session.startDateTime),
        endDateTime: toUtcIsoString(session.endDateTime),
        capacity: session.capacity,
        bookingEnabled: activity.bookingEnabled,
        bookingCutoffHours,
      }));

      const payload = {
        title: activity.title,
        description: activity.description || undefined,
        venueId: activity.venueId,
        status: activity.status,
        currency: activity.currency,
        paymentMethods: activity.paymentMethods,
        startDate: toUtcIsoString(first.startDateTime),
        endDate: toUtcIsoString(last.endDateTime),
        capacity,
        priceCents,
        sessions: normalizedSessions,
        ticketTypes: activity.ticketTypes,
        addOns: activity.addOns,
        notificationSettings: activity.notificationSettings,
        waitlistEnabled: activity.waitlistEnabled,
      };

      if (isEditing && eventId) {
        await clubsBookingApi.updateEvent(eventId, payload);
      } else {
        await clubsBookingApi.createEvent(payload);
      }
      if (options.navigateAfterSave !== false) {
        navigate('/activities');
      }
    } catch (err: any) {
      const messageText = err?.response?.data?.message;
      setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || err.message || 'Could not save activity');
    } finally {
      setSaving(false);
      setSavingSessionId('');
    }
  };

  const submitEvent = async (e: FormEvent) => {
    e.preventDefault();
    await saveActivity({ navigateAfterSave: true });
  };

  const saveSessionChanges = async (sessionId: string) => {
    setSavingSessionId(sessionId);
    await saveActivity({ navigateAfterSave: false });
  };

  return (
    <AdminShell title="Activities" subtitle={isEditing ? 'Edit activity' : 'Create activity'}>
      <Joyride
        run={showCreateTour && tourSteps.length > 0}
        stepIndex={tourStepIndex}
        steps={tourSteps}
        continuous
        showSkipButton
        disableScrolling
        callback={handleCreateTourCallback}
        styles={{
          options: {
            primaryColor: '#1F8F5F',
            zIndex: 10000,
          },
        }}
      />
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-2xl font-semibold">{isEditing ? 'Edit activity' : 'Create activity'}</h2>
          <Link to="/activities" className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">Back to list</Link>
        </div>

        <div className="mb-5 grid grid-cols-2 gap-2 md:grid-cols-4" data-tour-create-tabs>
          {steps.map((label, index) => (
            <button
              key={label}
              type="button"
              onClick={() => setStep(index)}
              className={`rounded-lg border px-3 py-2 text-sm ${step === index ? 'border-[#1F8F5F] bg-[#F3FBF7] text-[#1F8F5F]' : 'border-slate-300 bg-white text-slate-600'}`}
            >
              {String(index + 1).padStart(2, '0')} {label}
            </button>
          ))}
        </div>

        {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
        {eventLoading ? <p className="text-sm text-slate-500">Loading activity...</p> : (
          <form onSubmit={submitEvent} className="space-y-5">
            {step === 0 && (
              <div className="grid gap-4 md:grid-cols-2">
                <label className="text-sm text-slate-600">Title<input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={activity.title} onChange={(e) => setActivity((v) => ({ ...v, title: e.target.value }))} required data-tour-create-title /></label>
                <label className="text-sm text-slate-600 md:col-span-2">Description<textarea className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" rows={3} value={activity.description} onChange={(e) => setActivity((v) => ({ ...v, description: e.target.value }))} required /></label>
                <div className="md:col-span-2" data-tour-create-venue>
                  <div className="mb-1 flex items-center justify-between text-sm text-slate-600">
                    <span>Venue</span>
                    <div className="flex gap-2">
                      <button type="button" className="rounded-md border border-[#D8F1E2] bg-[#F3FBF7] px-3 py-1 text-xs font-medium text-[#1F8F5F] hover:bg-[#EAF7F0]" onClick={openCreateVenueModal}>Create new venue</button>
                      <button type="button" className="rounded-md border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50" onClick={openEditVenueModal} disabled={!selectedVenue}>Edit selected venue</button>
                    </div>
                  </div>
                  <select
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2"
                    value={activity.venueId}
                    onChange={(e) => handleVenueSelectChange(e.target.value)}
                    required
                    disabled={venuesLoading}
                    data-testid="activity-venue-select"
                  >
                    <option value="">{hasNoVenues ? 'No venues yet - create one below' : 'Select a venue'}</option>
                    {!venuesLoading && <option value={CREATE_NEW_VENUE_SELECT_VALUE}>+ Create new venue...</option>}
                    {venues.map((venue) => <option key={venue._id} value={venue._id}>{venue.name} - {venue.city}</option>)}
                  </select>
                  {hasNoVenues && (
                    <div
                      className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3"
                      data-testid="activity-no-venues-callout"
                    >
                      <p className="text-sm font-semibold text-amber-900">No venues yet</p>
                      <p className="mt-1 text-sm text-amber-800">
                        Create your first venue to continue setting up this activity.
                      </p>
                      <button
                        type="button"
                        className="mt-3 rounded-md bg-[#1F8F5F] px-3 py-2 text-sm font-semibold text-white hover:bg-[#19764F]"
                        onClick={openCreateVenueModal}
                      >
                        Create your first venue
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="rounded-lg border border-[#D8F1E2] bg-[#F3FBF7] px-3 py-2 text-sm font-medium text-[#1F8F5F] hover:bg-[#EAF7F0]"
                    onClick={() => setShowRecurringModal(true)}
                    data-tour-create-session-recurring
                  >
                    Add recurring sessions
                  </button>
                </div>

                <div className="grid gap-3 md:grid-cols-5" data-tour-create-session-form>
                  <label className="text-sm text-slate-600">
                    Start
                    <input
                      type="datetime-local"
                      className={`mt-1 w-full rounded-lg px-3 py-2 ${sessionFieldErrors.startDateTime ? 'border border-red-300 bg-red-50' : 'border border-slate-300'}`}
                      value={newSession.startDateTime}
                      onChange={(e) => {
                        const nextValue = e.target.value;
                        setNewSession((v) => ({ ...v, startDateTime: nextValue }));
                        if (sessionFieldErrors.startDateTime) {
                          setSessionFieldErrors((prev) => ({ ...prev, startDateTime: false }));
                        }
                        if (sessionFormError) {
                          setSessionFormError('');
                        }
                      }}
                    />
                  </label>
                  <label className="text-sm text-slate-600">
                    End
                    <input
                      type="datetime-local"
                      className={`mt-1 w-full rounded-lg px-3 py-2 ${sessionFieldErrors.endDateTime ? 'border border-red-300 bg-red-50' : 'border border-slate-300'}`}
                      value={newSession.endDateTime}
                      onChange={(e) => {
                        const nextValue = e.target.value;
                        setNewSession((v) => ({ ...v, endDateTime: nextValue }));
                        if (sessionFieldErrors.endDateTime) {
                          setSessionFieldErrors((prev) => ({ ...prev, endDateTime: false }));
                        }
                        if (sessionFormError) {
                          setSessionFormError('');
                        }
                      }}
                    />
                  </label>
                  <label className="text-sm text-slate-600">
                    Capacity
                    {newSession.unlimited ? (
                      <input className="mt-1 w-full rounded-lg border border-slate-300 bg-slate-100 px-3 py-2 text-slate-600" value="Unlimited" readOnly />
                    ) : (
                      <input
                        type="number"
                        min={1}
                        className={`mt-1 w-full rounded-lg px-3 py-2 ${sessionFieldErrors.capacity ? 'border border-red-300 bg-red-50' : 'border border-slate-300'}`}
                        value={newSession.capacity}
                        onChange={(e) => {
                          setNewSession((v) => ({ ...v, capacity: Number(e.target.value) }));
                          if (sessionFieldErrors.capacity) {
                            setSessionFieldErrors((prev) => ({ ...prev, capacity: false }));
                          }
                          if (sessionFormError) {
                            setSessionFormError('');
                          }
                        }}
                      />
                    )}
                  </label>
                  <label className="flex items-end gap-2 pb-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={newSession.unlimited}
                      onChange={(e) => {
                        setNewSession((v) => ({ ...v, unlimited: e.target.checked }));
                        if (e.target.checked && sessionFieldErrors.capacity) {
                          setSessionFieldErrors((prev) => ({ ...prev, capacity: false }));
                        }
                        if (sessionFormError) {
                          setSessionFormError('');
                        }
                      }}
                    />
                    Unlimited
                  </label>
                  <div className="flex items-end"><button type="button" className="w-full rounded-lg bg-[#1F8F5F] px-3 py-2 text-sm font-medium text-white hover:bg-[#1F8F5F]" onClick={addSession} data-tour-create-session-add>Add session</button></div>
                </div>
                {sessionFormError && (
                  <div
                    className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700"
                    data-testid="activity-session-form-error"
                  >
                    {sessionFormError}
                  </div>
                )}
                <div className="space-y-2">
                  {activity.sessions.map((session) => (
                    <div key={session.id} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm">
                      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(120px,160px)_auto_auto_auto] md:items-end">
                        <label className="text-xs font-medium uppercase tracking-wide text-slate-500">
                          Start
                          <input
                            type="datetime-local"
                            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-normal normal-case tracking-normal text-slate-900"
                            value={toDateTimeInputValue(session.startDateTime)}
                            onChange={(e) => updateSession(session.id, { startDateTime: e.target.value })}
                          />
                        </label>
                        <label className="text-xs font-medium uppercase tracking-wide text-slate-500">
                          End
                          <input
                            type="datetime-local"
                            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-normal normal-case tracking-normal text-slate-900"
                            value={toDateTimeInputValue(session.endDateTime)}
                            onChange={(e) => updateSession(session.id, { endDateTime: e.target.value })}
                          />
                        </label>
                        <label className="text-xs font-medium uppercase tracking-wide text-slate-500">
                          Capacity
                          {isUnlimitedCapacity(session.capacity) ? (
                            <input className="mt-1 w-full rounded-lg border border-slate-300 bg-slate-100 px-3 py-2 text-sm font-normal normal-case tracking-normal text-slate-600" value="Unlimited" readOnly />
                          ) : (
                            <input
                              type="number"
                              min={1}
                              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-normal normal-case tracking-normal text-slate-900"
                              value={session.capacity}
                              onChange={(e) => updateSession(session.id, { capacity: Number(e.target.value) })}
                            />
                          )}
                        </label>
                        <label className="flex items-center gap-2 pb-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={isUnlimitedCapacity(session.capacity)}
                            onChange={(e) => updateSession(session.id, { capacity: e.target.checked ? -1 : 20 })}
                          />
                          Unlimited
                        </label>
                        <button
                          type="button"
                          className="rounded-lg bg-[#1F8F5F] px-3 py-2 text-xs font-medium text-white hover:bg-[#18764F] disabled:cursor-not-allowed disabled:opacity-60"
                          disabled={saving}
                          onClick={() => void saveSessionChanges(session.id)}
                        >
                          {savingSessionId === session.id ? 'Saving...' : 'Save changes'}
                        </button>
                        <button
                          type="button"
                          className="pb-2 text-left text-xs font-medium text-red-700 hover:underline disabled:cursor-not-allowed disabled:text-slate-400 disabled:no-underline md:text-right"
                          disabled={Number(session.bookedSpots || 0) > 0}
                          title={Number(session.bookedSpots || 0) > 0 ? 'Sessions with bookings cannot be removed' : undefined}
                          onClick={() => requestRemoveSession(session)}
                        >
                          Remove
                        </button>
                      </div>
                      {typeof session.bookedSpots === 'number' && (
                        <p className="mt-2 text-xs text-slate-500">
                          {session.bookedSpots} booked, {isUnlimitedCapacity(session.capacity) ? 'unlimited spaces' : `${Math.max(0, Number(session.capacity || 0) - session.bookedSpots)} spaces remaining`}
                          {session.bookedSpots > 0 ? '. This session cannot be removed.' : ''}
                        </p>
                      )}
                    </div>
                  ))}
                  {!activity.sessions.length && <p className="text-sm text-slate-500">No sessions yet.</p>}
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-3">
                {!activity.ticketTypes.length && (
                  <div className="rounded-xl border border-[#D8F1E2] bg-[#F6FAF7] p-4" data-tour-create-ticket-quick>
                    <h3 className="text-sm font-semibold text-[#1E293B]">Quick ticket setup</h3>
                    <p className="mt-1 text-xs text-[#2F6B52]">
                      Set prices once and create one ticket per session automatically, with an optional all-sessions ticket.
                    </p>
                    <div className="mt-3 space-y-3">
                      <div className="grid gap-3 md:grid-cols-2">
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={quickTicketSetup.includePerSession}
                            onChange={(e) => setQuickTicketSetup((v) => ({ ...v, includePerSession: e.target.checked }))}
                          />
                          Add one ticket per session
                        </label>
                        <label className="text-sm text-slate-700">
                          Per-session price
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 disabled:bg-slate-100"
                            value={quickTicketSetup.perSessionPrice}
                            onChange={(e) => setQuickTicketSetup((v) => ({ ...v, perSessionPrice: e.target.value }))}
                            disabled={!quickTicketSetup.includePerSession}
                          />
                        </label>
                      </div>
                      <div className="grid gap-3 md:grid-cols-2">
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={quickTicketSetup.includeAllSessions}
                            onChange={(e) => setQuickTicketSetup((v) => ({ ...v, includeAllSessions: e.target.checked }))}
                          />
                          Add all-sessions ticket
                        </label>
                        <label className="text-sm text-slate-700">
                          All-sessions price
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 disabled:bg-slate-100"
                            value={quickTicketSetup.allSessionsPrice}
                            onChange={(e) => setQuickTicketSetup((v) => ({ ...v, allSessionsPrice: e.target.value }))}
                            disabled={!quickTicketSetup.includeAllSessions}
                          />
                        </label>
                      </div>
                      <div className="flex justify-end">
                        <button
                          type="button"
                          className="rounded-lg bg-[#1F8F5F] px-4 py-2 text-sm font-medium text-white hover:bg-[#1F8F5F]"
                          onClick={createQuickDefaultTickets}
                        >
                          Create default tickets
                        </button>
                      </div>
                    </div>
                    {!activity.sessions.length && <p className="mt-2 text-xs text-amber-700">Add sessions first to generate tickets.</p>}
                  </div>
                )}

                {activity.ticketTypes.length > 0 && (
                  <>
                    <div className="grid gap-3 md:grid-cols-5">
                      <label className="text-sm text-slate-600 md:col-span-2">Ticket name<input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={newTicket.name} onChange={(e) => setNewTicket((v) => ({ ...v, name: e.target.value }))} /></label>
                      <label className="text-sm text-slate-600">Mode<select className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={newTicket.mode} onChange={(e) => setNewTicket((v) => ({ ...v, mode: e.target.value as TicketMode, sessionIds: [] }))}><option value="all_sessions">All sessions</option><option value="specific_sessions">Specific sessions</option></select></label>
                      <label className="text-sm text-slate-600">Price<input type="number" min={0} step="0.01" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={newTicket.price} onChange={(e) => setNewTicket((v) => ({ ...v, price: e.target.value }))} /></label>
                      <div className="flex items-end"><button type="button" className="w-full rounded-lg bg-[#1F8F5F] px-3 py-2 text-sm font-medium text-white hover:bg-[#1F8F5F]" onClick={addTicket}>Add ticket</button></div>
                    </div>
                    {newTicket.mode === 'specific_sessions' && (
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <p className="mb-2 text-sm font-medium text-slate-700">Allow these sessions</p>
                        <div className="space-y-1">
                          {activity.sessions.map((session) => (
                            <label key={session.id} className="flex items-center gap-2 text-sm">
                              <input type="checkbox" checked={newTicket.sessionIds.includes(session.id)} onChange={(e) => setNewTicket((v) => ({ ...v, sessionIds: e.target.checked ? [...v.sessionIds, session.id] : v.sessionIds.filter((id) => id !== session.id) }))} />
                              <span>{new Date(session.startDateTime).toLocaleString('en-GB')}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="space-y-2">
                      {activity.ticketTypes.map((ticket) => (
                        <div key={ticket.id} className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                          <span>{ticket.name} ({ticket.mode === 'all_sessions' ? 'All sessions' : `${ticket.sessionIds?.length || 0} specific sessions`}) - {formatPrice(ticket.priceCents, activity.currency)}</span>
                          <button type="button" className="text-xs text-red-700 hover:underline" onClick={() => setActivity((v) => ({ ...v, ticketTypes: v.ticketTypes.filter((item) => item.id !== ticket.id) }))}>Remove</button>
                        </div>
                      ))}
                      {!activity.ticketTypes.length && <p className="text-sm text-slate-500">No ticket types yet.</p>}
                    </div>
                    <div className="mt-4 border-t border-slate-200 pt-4">
                      <h3 className="text-sm font-semibold text-slate-800">Add-ons (optional)</h3>
                      <p className="mt-1 text-xs text-slate-500">Optional extras purchasable per selected session during checkout.</p>
                      <div className="mt-3 grid gap-3 md:grid-cols-4">
                        <label className="text-sm text-slate-600 md:col-span-2">Add-on name<input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={newAddOn.name} onChange={(e) => setNewAddOn((v) => ({ ...v, name: e.target.value }))} /></label>
                        <label className="text-sm text-slate-600">Price<input type="number" min={0} step="0.01" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={newAddOn.price} onChange={(e) => setNewAddOn((v) => ({ ...v, price: e.target.value }))} /></label>
                        <div className="flex items-end"><button type="button" className="w-full rounded-lg bg-[#1F8F5F] px-3 py-2 text-sm font-medium text-white hover:bg-[#1F8F5F]" onClick={addAddOn}>Add add-on</button></div>
                      </div>
                      <div className="mt-3 space-y-2">
                        {activity.addOns.map((addOn) => (
                          <div key={addOn.id} className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                            <span>{addOn.name} - {formatPrice(addOn.priceCents, activity.currency)} / session</span>
                            <button type="button" className="text-xs text-red-700 hover:underline" onClick={() => setActivity((v) => ({ ...v, addOns: v.addOns.filter((item) => item.id !== addOn.id) }))}>Remove</button>
                          </div>
                        ))}
                        {!activity.addOns.length && <p className="text-sm text-slate-500">No add-ons yet.</p>}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {step === 3 && (
              <div className="grid gap-4 md:grid-cols-2">
                <label className="text-sm text-slate-600">Status<select className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={activity.status} onChange={(e) => setActivity((v) => ({ ...v, status: e.target.value as EventStatus }))}><option value="draft">Not live</option><option value="published">Live</option></select></label>
                <label className="text-sm text-slate-600">Currency<select className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2" value={activity.currency} onChange={(e) => setActivity((v) => ({ ...v, currency: e.target.value }))}><option value="GBP">GBP (&pound;)</option></select></label>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 md:col-span-2" data-tour-create-settings>
                  <h3 className="text-sm font-semibold text-slate-800">Payment options</h3>
                  <p className="mt-1 text-xs text-slate-500">
                    Choose which payment methods parents can use for this activity.
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-4">
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                      <input
                        type="checkbox"
                        checked={activity.paymentMethods.includes('cash')}
                        onChange={(e) =>
                          setActivity((v) => ({
                            ...v,
                            paymentMethods: e.target.checked
                              ? (Array.from(new Set([...v.paymentMethods, 'cash'])) as Array<'card' | 'cash'>)
                              : v.paymentMethods.filter((method) => method !== 'cash'),
                          }))
                        }
                      />
                      Cash
                    </label>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                      <input
                        type="checkbox"
                        checked={activity.paymentMethods.includes('card')}
                        onChange={(e) =>
                          setActivity((v) => ({
                            ...v,
                            paymentMethods: e.target.checked
                              ? (Array.from(new Set([...v.paymentMethods, 'card'])) as Array<'card' | 'cash'>)
                              : v.paymentMethods.filter((method) => method !== 'card'),
                          }))
                        }
                      />
                      Card
                    </label>
                  </div>
                  <p className="mt-2 text-xs text-slate-600">
                    Card payments are only available to parents when Stripe Connect is fully set up in Settings.
                  </p>
                  {activity.paymentMethods.includes('card') && !stripeStatusLoading && !stripeCardReady && (
                    <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                      Stripe is not connected for this account yet. Parents will only see non-card payment options until Stripe setup is complete.
                    </p>
                  )}
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 md:col-span-2">
                  <h3 className="text-sm font-semibold text-slate-800">Session booking window</h3>
                  <p className="mt-1 text-xs text-slate-500">
                    These settings apply to all sessions in this activity.
                  </p>
                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                      <input
                        type="checkbox"
                        checked={activity.bookingEnabled}
                        onChange={(e) => setActivity((v) => ({ ...v, bookingEnabled: e.target.checked }))}
                      />
                      Allow parents to book sessions
                    </label>
                    <label className="text-sm text-slate-600">
                      Cut-off (hours before session start)
                      <input
                        type="number"
                        min={0}
                        className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 disabled:bg-slate-100"
                        value={activity.bookingCutoffHours}
                        onChange={(e) => setActivity((v) => ({ ...v, bookingCutoffHours: e.target.value }))}
                        placeholder="At session start time"
                        disabled={!activity.bookingEnabled}
                      />
                    </label>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 md:col-span-2">
                      <input
                        type="checkbox"
                        checked={activity.waitlistEnabled}
                        onChange={(e) => setActivity((v) => ({ ...v, waitlistEnabled: e.target.checked }))}
                      />
                      Enable waitlist when a session reaches capacity
                    </label>
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 md:col-span-2">
                  <h3 className="text-sm font-semibold text-slate-800">Attendance chaser reminders</h3>
                  <p className="mt-1 text-xs text-slate-500">
                    Send parents reminders before each session to confirm attendance and reduce no-shows.
                  </p>

                  <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                      <input
                        type="checkbox"
                        checked={activity.notificationSettings.emailEnabled}
                        onChange={(e) =>
                          setActivity((v) => ({
                            ...v,
                            notificationSettings: {
                              ...v.notificationSettings,
                              emailEnabled: e.target.checked,
                              smsEnabled: false,
                            },
                          }))
                        }
                      />
                      Send reminder emails
                    </label>
                    <label className="mt-3 block text-sm text-slate-600">
                      Timing
                      <select
                        className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2"
                        value={activity.notificationSettings.emailLeadMinutes}
                        onChange={(e) =>
                          setActivity((v) => ({
                            ...v,
                            notificationSettings: {
                              ...v.notificationSettings,
                              emailLeadMinutes: Number(e.target.value),
                            },
                          }))
                        }
                      >
                        {reminderLeadOptions.map((option) => (
                          <option key={`email-${option.value}`} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between border-t border-slate-100 pt-4">
              <button type="button" className="rounded-lg border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50 disabled:opacity-50" onClick={() => setStep((v) => Math.max(0, v - 1))} disabled={step === 0}>Back</button>
              <div className="flex gap-2">
                {step < steps.length - 1 && (
                  <button type="button" className="rounded-lg bg-[#1F8F5F] px-4 py-2 text-sm font-medium text-white hover:bg-[#1F8F5F]" onClick={() => setStep((v) => Math.min(steps.length - 1, v + 1))}>Next</button>
                )}
                {step === steps.length - 1 && (
                  <button type="submit" className="rounded-lg bg-[#1F8F5F] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#1F8F5F] disabled:opacity-60" disabled={saving} data-tour-create-submit>{saving ? 'Saving...' : isEditing ? 'Save activity' : 'Create activity'}</button>
                )}
              </div>
            </div>
          </form>
        )}
      </section>

      {showRecurringModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4">
          <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white shadow-xl">
            <div className="border-b border-slate-100 px-6 py-4"><h3 className="text-xl font-semibold">Add recurring sessions</h3></div>
            <div className="grid gap-3 px-6 py-4 md:grid-cols-2">
              <label className="text-sm text-slate-600">First date<input type="date" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={recurringForm.startDate} onChange={(e) => setRecurringForm((v) => ({ ...v, startDate: e.target.value }))} /></label>
              <label className="text-sm text-slate-600">Repeat<select className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={recurringForm.frequency} onChange={(e) => setRecurringForm((v) => ({ ...v, frequency: e.target.value as 'weekly' | 'daily' }))}><option value="weekly">Weekly</option><option value="daily">Daily</option></select></label>
              <label className="text-sm text-slate-600">Start time<input type="time" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={recurringForm.startTime} onChange={(e) => setRecurringForm((v) => ({ ...v, startTime: e.target.value }))} /></label>
              <label className="text-sm text-slate-600">End time<input type="time" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={recurringForm.endTime} onChange={(e) => setRecurringForm((v) => ({ ...v, endTime: e.target.value }))} /></label>
              <label className="text-sm text-slate-600">Occurrences<input type="number" min={1} max={60} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={recurringForm.occurrences} onChange={(e) => setRecurringForm((v) => ({ ...v, occurrences: Number(e.target.value) }))} /></label>
              <label className="text-sm text-slate-600">
                Capacity
                {recurringForm.unlimited ? (
                  <input className="mt-1 w-full rounded-lg border border-slate-300 bg-slate-100 px-3 py-2 text-slate-600" value="Unlimited" readOnly />
                ) : (
                  <input type="number" min={1} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={recurringForm.capacity} onChange={(e) => setRecurringForm((v) => ({ ...v, capacity: Number(e.target.value) }))} />
                )}
              </label>
              <label className="flex items-end gap-2 pb-2 text-sm text-slate-700 md:col-span-2"><input type="checkbox" checked={recurringForm.unlimited} onChange={(e) => setRecurringForm((v) => ({ ...v, unlimited: e.target.checked }))} />Unlimited capacity</label>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">
              <button type="button" className="rounded-md border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50" onClick={() => setShowRecurringModal(false)}>Cancel</button>
              <button type="button" className="rounded-md bg-[#1F8F5F] px-4 py-2 text-sm font-medium text-white hover:bg-[#1F8F5F]" onClick={addRecurringSessions}>Add sessions</button>
            </div>
          </div>
        </div>
      )}

      {showVenueModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4">
          <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white shadow-xl">
            <div className="border-b border-slate-100 px-6 py-4"><h3 className="text-2xl font-semibold">{venueModalMode === 'edit' ? 'Edit venue' : 'Create venue'}</h3></div>
            <form onSubmit={submitVenue} className="grid gap-3 px-6 py-4">
              <label className="text-sm text-slate-600">Name<input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={venueForm.name} onChange={(e) => setVenueForm((v) => ({ ...v, name: e.target.value }))} required /></label>
              <label className="text-sm text-slate-600">Address line 1<input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={venueForm.addressLine1} onChange={(e) => setVenueForm((v) => ({ ...v, addressLine1: e.target.value }))} required /></label>
              <label className="text-sm text-slate-600">Address line 2<input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={venueForm.addressLine2} onChange={(e) => setVenueForm((v) => ({ ...v, addressLine2: e.target.value }))} /></label>
              <div className="grid gap-3 md:grid-cols-2">
                <label className="text-sm text-slate-600">City<input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={venueForm.city} onChange={(e) => setVenueForm((v) => ({ ...v, city: e.target.value }))} required /></label>
                <label className="text-sm text-slate-600">Postal code<input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={venueForm.postcode} onChange={(e) => setVenueForm((v) => ({ ...v, postcode: e.target.value }))} required /></label>
              </div>
              {venueError && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{venueError}</div>}
              <div className="mt-1 flex justify-end gap-2 border-t border-slate-100 pt-4">
                <button type="button" className="rounded-md border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50" onClick={closeVenueModal}>Cancel</button>
                <button className="rounded-md bg-[#1F8F5F] px-4 py-2 text-sm font-medium text-white hover:bg-[#1F8F5F] disabled:opacity-60" disabled={venueSaving}>{venueSaving ? 'Saving...' : venueModalMode === 'edit' ? 'Save changes' : 'Submit'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminShell>
  );
};

export default ActivityCreatePage;
